import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import JsSIP from 'jssip';
import { VoIPContext } from './VoIPContext';
import { UAState, CallState } from '../types';
import { useJsSipConfig } from '../hooks/useJsSipConfig';
import { playRingtone, stopRingtone } from '../utils/audioManager';
import { useCallRecorder } from '../hooks/useCallRecorder';
import * as userSelectors from '../../../selectors/userSelectors';
import { getSituationLogs } from '../../../redux/thunks/monitoring';

export const VoIPProvider = ({ children }) => {
  const [uaState, setUaState] = useState(UAState.DISCONNECTED);
  const [callState, setCallState] = useState(CallState.IDLE);
  const [currentCall, setCurrentCall] = useState(null);
  const [isDialerOpen, setIsDialerOpen] = useState(false);
  const [isDialerMinimized, setIsDialerMinimized] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [callFailureReason, setCallFailureReason] = useState(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const situationIdRef = useRef(null);
  const callIdRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const localStreamRef = useRef(null);
  const callEndedRef = useRef(false);

  const userId = useSelector(userSelectors.getUserId());
  const dispatch = useDispatch();

  const { startRecording, stopRecording } = useCallRecorder();

  const { config, hasJsSipExtension } = useJsSipConfig();

  // UA Event Handlers
  const handleConnecting = useCallback(() => {
    setUaState(UAState.CONNECTING);
  }, []);

  const handleConnected = useCallback(() => {
    setUaState(UAState.CONNECTED);
  }, []);

  const handleDisconnected = useCallback(() => {
    setUaState(UAState.DISCONNECTED);
    console.warn('VoIP disconnected, attempting reconnect');
  }, []);

  const handleRegistered = useCallback(() => {
    setUaState(UAState.REGISTERED);
    console.info('✅ VoIP registered successfully', { 
      extension: config?.extension,
      displayName: config?.displayName,
      wsServer: config?.wsServer 
    });
  }, [config?.extension, config?.displayName, config?.wsServer]);

  const handleUnregistered = useCallback(() => {
    setUaState(UAState.UNREGISTERED);
  }, []);

  const handleRegistrationFailed = useCallback((e) => {
    setUaState(UAState.REGISTRATION_FAILED);
    console.error('VoIP registration failed', { error: e.cause });
  }, []);

  // Call cleanup
  const handleCallEnded = useCallback(() => {
    if (callEndedRef.current) return;
    callEndedRef.current = true;

    setCallState(CallState.ENDED);
    stopRingtone();
    setIsRecording(false);

    const doCleanup = () => {
      setTimeout(() => {
        callEndedRef.current = false;
        setIsUploading(false);
        setCallState(CallState.IDLE);
        setCurrentCall(null);
        setIsDialerOpen(false);
        setIsDialerMinimized(false);
        setIsMuted(false);
        setIsOnHold(false);
        setCallFailureReason(null);
        sessionRef.current = null;
        situationIdRef.current = null;
        callIdRef.current = null;
        callStartTimeRef.current = null;
        remoteStreamRef.current = null;
        localStreamRef.current = null;
      }, 2000);
    };

    if (situationIdRef.current) {
      const situationId = situationIdRef.current;
      setIsUploading(true);
      stopRecording()
        .then(() => { dispatch(getSituationLogs(situationId)); })
        .catch((err) => console.error('Error stopping call recording', { error: err.message }))
        .finally(doCleanup);
    } else {
      doCleanup();
    }
  }, [stopRecording, dispatch]);

  const handleCallFailed = useCallback((e) => {
    console.error('Call failed', { originator: e.originator, cause: e.cause, message: e.message });
    setCallFailureReason(e.cause);
    handleCallEnded();
  }, [handleCallEnded]);

  // Session event handlers
  const setupSessionHandlers = useCallback((session) => {
    session.on('peerconnection', (e) => {
      const peerconnection = e.peerconnection;

      // Log ICE connection state changes
      peerconnection.oniceconnectionstatechange = () => {
      };

      // Log connection state changes
      peerconnection.onconnectionstatechange = () => {
      };

      peerconnection.ontrack = (event) => {

        const track = event.track;

        // Listen for track unmute event
        track.onunmute = () => {
          if (audioRef.current) {
            audioRef.current.play().catch(err => console.error('Play error on unmute:', err));
          }
        };

        if (event.streams && event.streams[0] && audioRef.current) {
          const stream = event.streams[0];
          remoteStreamRef.current = stream;

          audioRef.current.srcObject = stream;
          audioRef.current.volume = 1.0;
          audioRef.current.muted = false;

          audioRef.current.play().then(() => {
          }).catch((error) => {
            console.error('❌ Error playing remote audio:', error);
          });
        }

      };
    });

    // Hold/unhold event handlers
    session.on('hold', (data) => {
      setIsOnHold(true);
      setCallState(CallState.ON_HOLD);
    });

    session.on('unhold', (data) => {
      setIsOnHold(false);
      setCallState(CallState.CONNECTED);
      
      // Ensure audio is playing after unhold
      if (audioRef.current && audioRef.current.srcObject) {
        audioRef.current.play().catch(err => console.error('Error resuming audio:', err));
      }
    });

    session.on('accepted', () => {
      setCallState(CallState.CONNECTED);
      const startTime = Date.now();
      callStartTimeRef.current = startTime;
      setCurrentCall((prev) => ({
        ...prev,
        startTime,
      }));
      stopRingtone();

      if (situationIdRef.current) {
        const senders = session.connection?.getSenders?.() || [];
        const audioSender = senders.find((s) => s.track && s.track.kind === 'audio');
        if (audioSender && audioSender.track) {
          localStreamRef.current = new MediaStream([audioSender.track]);
        }

        startRecording({
          situationId: situationIdRef.current,
          callId: callIdRef.current,
          localStream: localStreamRef.current,
          remoteStream: remoteStreamRef.current,
          direction: session.direction,
          calledNumber: currentCall?.phoneNumber || null,
          extension: config?.extension || null,
          userId,
        }).catch((err) =>
          console.error('Failed to start recording on accept', { error: err.message }),
        );
        setIsRecording(true);
      }
    });

    session.on('ended', handleCallEnded);
    session.on('failed', handleCallFailed);
  }, [handleCallEnded, handleCallFailed, startRecording, config?.extension]);

  // Handle incoming calls
  const handleNewRTCSession = useCallback(
    (data) => {
      const session = data.session;

      if (session.direction === 'incoming') {
        sessionRef.current = session;
        callIdRef.current = uuidv4();
        setCallState(CallState.INCOMING);
        setCurrentCall({
          phoneNumber: session.remote_identity.uri.user,
          contactInfo: null,
          direction: 'incoming',
        });
        setIsDialerOpen(true);
        playRingtone();
        setupSessionHandlers(session);
        
        // Show browser notification
        if (Notification.permission === 'granted') {
          const notification = new Notification('Incoming Call', {
            body: `Call from ${session.remote_identity.uri.user}`,
            tag: 'voip-call',
            requireInteraction: true,
          });
          
          notification.onclick = () => {
            window.focus();
            setIsDialerMinimized(false);
          };
        }
      }
    },
    [setupSessionHandlers]
  );

  // Initialize JsSIP User Agent
  useEffect(() => {
    if (!config || !hasJsSipExtension) return;

    // Enable JsSIP debug mode
    JsSIP.debug.enable('JsSIP:*');

    // A console.log monkey-patch used to live here, purely to prefix messages
    // containing 'INVITE', and it was never restored. DevTools attributes each
    // console entry to the innermost frame that called the native method, so
    // EVERY console.log in the app was reported as VoIPProvider.jsx:265 —
    // including monitoring's, which made VoIP look like the cause of monitoring
    // disconnects it had nothing to do with. It also nested on every remount,
    // since MainLayout renders VoIPProvider in two branches.

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Strip protocol prefix (wss://, ws://) and port from a URI to get just the domain
    const extractDomain = (uri) => {
      if (!uri) return '';
      return uri.replace(/^wss?:\/\//, '').split(':')[0];
    };

    const sipDomain = extractDomain(config.realm) || extractDomain(config.wsServer);
    const socket = new JsSIP.WebSocketInterface(config.wsServer);
    const sipUri = `sip:${config.extension}@${sipDomain}`;

    // Sanitize display name to ASCII-only characters to avoid SIP parsing errors
    const sanitizeDisplayName = (name) => {
      if (!name) return name;
      return name
        .normalize('NFD') // Decompose accented characters
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
        .replace(/[^\x20-\x7E]/g, '') // Remove non-ASCII characters
        .replace(/["']/g, '') // Remove quotes to avoid parsing issues
        .trim();
    };

    const sanitizedDisplayName = sanitizeDisplayName(config.displayName);

    const configuration = {
      sockets: [socket],
      uri: sipUri,
      password: config.password,
      display_name: sanitizedDisplayName,
      session_timers: false,
      register: true,
    };

    let ua;
    try {
      ua = new JsSIP.UA(configuration);
    } catch (err) {
      console.error('VoIP: Failed to create JsSIP User Agent', { error: err.message, sipUri });
      return;
    }

    uaRef.current = ua;

    ua.on('connecting', handleConnecting);
    ua.on('connected', handleConnected);
    ua.on('disconnected', handleDisconnected);
    ua.on('registered', handleRegistered);
    ua.on('unregistered', handleUnregistered);
    ua.on('registrationFailed', handleRegistrationFailed);
    ua.on('newRTCSession', handleNewRTCSession);

    ua.start();

    return () => {
      if (uaRef.current) {
        uaRef.current.stop();
      }
    };
  }, [
    config,
    hasJsSipExtension,
    handleConnecting,
    handleConnected,
    handleDisconnected,
    handleRegistered,
    handleUnregistered,
    handleRegistrationFailed,
    handleNewRTCSession,
  ]);

  // Make outgoing call
  const makeCall = useCallback(
    async (phoneNumber, contactInfo = null, situationId = null) => {
      if (!uaRef.current || uaState !== UAState.REGISTERED) {
        console.error('Cannot make call: not registered');
        return;
      }

      situationIdRef.current = situationId;
      callIdRef.current = uuidv4();

      // Show dialer immediately with outgoing state
      setCurrentCall({
        phoneNumber,
        contactInfo,
        direction: 'outgoing',
        startTime: null,
      });
      setCallState(CallState.OUTGOING);
      setIsDialerOpen(true);

      // Check microphone permission and capture local stream
      let localStream = null;
      try {
        localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        localStreamRef.current = localStream;
      } catch (error) {
        console.error('Microphone permission denied', { error: error.message });
        handleCallEnded();
        return;
      }

      const eventHandlers = {
        progress: () => setCallState(CallState.OUTGOING),
        peerconnection: (e) => {
          const peerconnection = e.peerconnection;

          peerconnection.ontrack = (event) => {
            if (event.streams && event.streams[0] && audioRef.current) {
              remoteStreamRef.current = event.streams[0];
              audioRef.current.srcObject = event.streams[0];
              audioRef.current.volume = 1.0;
              audioRef.current.muted = false;
              audioRef.current.play().catch((error) => {
                console.error('Error playing audio:', error);
              });
            }
          };
        },
        accepted: () => {
          const startTime = Date.now();
          callStartTimeRef.current = startTime;
          setCallState(CallState.CONNECTED);
          setCurrentCall((prev) => ({ ...prev, startTime }));

          if (situationIdRef.current) {
            startRecording({
              situationId: situationIdRef.current,
              callId: callIdRef.current,
              localStream: localStreamRef.current,
              remoteStream: remoteStreamRef.current,
              direction: 'outbound',
              calledNumber: phoneNumber,
              extension: config?.extension || null,
              userId,
            }).catch((err) =>
              console.error('Failed to start recording on accept', { error: err.message }),
            );
            setIsRecording(true);
          }
        },
        // ended, failed → handled by setupSessionHandlers
      };

      const options = {
        eventHandlers,
        mediaConstraints: { audio: true, video: false },
      };

      const normalizeOutboundNumber = (value) => {
        if (value == null) return '';
        const trimmed = String(value).trim();
        if (!trimmed) return '';
        if (trimmed.startsWith('+')) {
          return `00${trimmed.slice(1)}`;
        }
        return trimmed;
      };

      const normalizedNumber = normalizeOutboundNumber(phoneNumber);

      // Apply prefix if configured
      const prefixedPhoneNumber =
        config.prefix && config.prefix.trim()
          ? `${config.prefix}${normalizedNumber}`
          : normalizedNumber;

      const targetDomain = (config.realm || config.wsServer || '').replace(/^wss?:\/\//, '').split(':')[0];
      const sipTarget = `sip:${prefixedPhoneNumber}@${targetDomain}`;
      const session = uaRef.current.call(sipTarget, options);
      sessionRef.current = session;

      // Set up hold/unhold and other session event handlers
      setupSessionHandlers(session);
    },
    [uaState, config, handleCallEnded, setupSessionHandlers, startRecording, userId]
  );

  // Answer incoming call
  const answerCall = useCallback(() => {
    if (!sessionRef.current) return;

    const options = {
      mediaConstraints: { audio: true, video: false },
    };

    // Set up track handler BEFORE answering
    sessionRef.current.on('peerconnection', (e) => {
      const peerconnection = e.peerconnection;

      peerconnection.ontrack = (event) => {
        
        if (event.streams && event.streams[0] && audioRef.current) {
          audioRef.current.srcObject = event.streams[0];
          audioRef.current.volume = 1.0;
          audioRef.current.muted = false;
          
          audioRef.current.play().then(() => {
          }).catch((error) => {
            console.error('❌ [Answer] Error playing audio:', error);
          });
        }
        
      };
    });

    sessionRef.current.answer(options);
    stopRingtone();
  }, []);

  // Hang up call
  const hangUp = useCallback(() => {
    if (!sessionRef.current) return;

    sessionRef.current.terminate();
    handleCallEnded();
  }, [handleCallEnded]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (!sessionRef.current) return;

    if (isMuted) {
      sessionRef.current.unmute();
    } else {
      sessionRef.current.mute();
    }
    setIsMuted(!isMuted);
  }, [isMuted]);

  // Toggle hold
  const toggleHold = useCallback(() => {
    if (!sessionRef.current) {
      console.error('❌ Cannot toggle hold: no active session');
      return;
    }


    try {
      if (isOnHold) {
        // Resume call
        sessionRef.current.unhold();
        // State will be updated by 'unhold' event listener
      } else {
        // Put call on hold
        sessionRef.current.hold();
        // State will be updated by 'hold' event listener
      }
    } catch (error) {
      console.error('❌ Failed to toggle hold:', error);
    }
  }, [isOnHold]);

  // Dialer UI controls
  const openDialer = useCallback(() => {
    setIsDialerOpen(true);
    setIsDialerMinimized(false);
  }, []);

  const closeDialer = useCallback(() => {
    if (callState !== CallState.IDLE) return;
    setIsDialerOpen(false);
  }, [callState]);

  const minimizeDialer = useCallback(() => {
    setIsDialerMinimized(true);
  }, []);

  const maximizeDialer = useCallback(() => {
    setIsDialerMinimized(false);
  }, []);

  const toggleDebugPanel = useCallback(() => {
    setShowDebugPanel((prev) => !prev);
  }, []);

  // Expose VoIP functions to window for browser console testing
  useEffect(() => {
    window.__voip = { makeCall, answerCall, hangUp, toggleMute, toggleHold };
    return () => { delete window.__voip; };
  }, [makeCall, answerCall, hangUp, toggleMute, toggleHold]);

  const value = {
    uaState,
    isRegistered: uaState === UAState.REGISTERED,
    hasJsSipExtension,
    callState,
    currentCall,
    callFailureReason,
    isMuted,
    isOnHold,
    makeCall,
    answerCall,
    hangUp,
    toggleMute,
    toggleHold,
    isDialerOpen,
    isDialerMinimized,
    openDialer,
    closeDialer,
    minimizeDialer,
    maximizeDialer,
    showDebugPanel,
    toggleDebugPanel,
    isRecording,
    isUploading,
  };

  return (
    <VoIPContext.Provider value={value}>
      {children}
      {/* Hidden audio element for remote stream */}
      <audio ref={audioRef} style={{ display: 'none' }} autoPlay />
    </VoIPContext.Provider>
  );
};
